import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { baseURL } from '../api/config';
import ProfessionalSpreadCard from '../components/ProfessionalSpreadCard';
import * as signalR from '@microsoft/signalr';
import { GetBetaSpreadConfigs, GetPairStatistics } from '../api/betaspread';

interface ServiceStatus {
  name: string;
  status: string;
  port: number;
  latency?: string;
  connections?: number;
  details?: string;
  ticksReceived?: number;
  spreadsCalculated?: number;
  spreadsRejected?: number;
  // Phase 1.6D: Enhanced gRPC monitoring metrics
  ticksDropped?: number;
  ticksFiltered?: number;
  dropRate?: number; // Percentage
  ticksValid?: number;
  // Phase 1.6E: Enhanced TimescaleDB storage metrics
  ticksWritten?: number;
  ticksBuffered?: number;
  ticksFailed?: number;
  channelCapacity?: number;
  bufferUtilization?: number; // Percentage
  failureRate?: number; // Percentage
}

interface DashboardStats {
  ticksPerSecond: number;
  spreadsPerMinute: number;
  memoryUsageMB: number;
  uptimeHours: number;
  downtimeMinutes: number;
  status: string;
  cpu: string;
  dataRate: string;
  activeSymbols: number;
  backfillJobs: number;
  backfillProgress: number;
  gapsDetected: number;
  gapsResolved: number;

  // Phase 1.6A: Optional fields for enhanced KPI cards
  activePairs?: number;  // Count of active spread pairs
}

// Phase 1.6B: 24H BetaSpread Statistics Interface
interface Pair24HStats {
  pairName: string;
  symbol1: string;
  symbol2: string;
  high24h: number | null;
  low24h: number | null;
  range24h: number | null;
  yesterdayHigh: number | null;
  yesterdayLow: number | null;
  yesterdayRange: number | null;
  avgVolatility: number | null;
  dataPoints: number;
  lastUpdate: Date;
  error?: string;
}

const Dashboard: React.FC = () => {
  console.log('🔥 DASHBOARD V4.0 - FINAL ULTRA-COMPACT BLOTTER - ALL FIXES APPLIED 🔥');

  const [stats, setStats] = useState<DashboardStats>({
    ticksPerSecond: 0,
    spreadsPerMinute: 0,
    memoryUsageMB: 0,
    uptimeHours: 0,
    downtimeMinutes: 0,
    status: 'LOADING',
    cpu: '0%',
    dataRate: '0 MB/s',
    activeSymbols: 0,
    backfillJobs: 0,
    backfillProgress: 0,
    gapsDetected: 0,
    gapsResolved: 0
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [sparklineData, setSparklineData] = useState<number[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Phase 1: Professional Spread Analytics - Data Layer
  const [betaSpreadData, setBetaSpreadData] = useState<Map<string, any>>(new Map());
  const [expandedPairs, setExpandedPairs] = useState<Set<string>>(new Set());
  const spreadDataIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Phase 1.6B: 24H BetaSpread Statistics Monitor
  const [pair24HStats, setPair24HStats] = useState<Pair24HStats[]>([]);
  const [stats24HLoading, setStats24HLoading] = useState(false);
  const stats24HIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Phase 10B: SignalR Real-Time Connection
  const [signalRConnected, setSignalRConnected] = useState(false);
  const signalRConnectionRef = useRef<signalR.HubConnection | null>(null);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      // Fetch REAL metrics from /api/admin/* endpoints
      const metricsRes = await axios.get(`${baseURL}/api/admin/metrics`).catch(() => null);
      const healthRes = await axios.get(`${baseURL}/api/admin/health`).catch(() => null);
      const configsRes = await axios.get(`${baseURL}/api/betaspread/configs`).catch(() => null);

      // Extract REAL data from API responses
      const metrics = metricsRes?.data || {};
      const health = healthRes?.data || {};
      const configs = configsRes?.data || [];

      // Phase 10C.2: Use structured fields from API (no regex parsing needed)
      const apiServices = health.services || [];
      const mappedServices = apiServices.map((svc: any) => {
        const service: any = {
          name: svc.name,
          status: svc.status.toLowerCase(),
          port: svc.name.includes('gRPC') ? 5101 : (svc.name.includes('TimescaleDB') ? 5432 : 3308),
          latency: svc.latency || undefined,
          connections: svc.connections || undefined,
          details: svc.details,
          // Phase 10C.2: Direct field access (backend provides structured fields)
          ticksReceived: svc.ticksReceived || 0,
          spreadsCalculated: svc.spreadsCalculated || 0,
          spreadsRejected: svc.spreadsRejected || 0,
          // Phase 1.6D: Enhanced gRPC metrics
          ticksDropped: svc.ticksDropped || 0,
          ticksFiltered: svc.ticksFiltered || 0,
          dropRate: svc.dropRate || 0,
          ticksValid: svc.ticksValid || 0,
          // Phase 1.6E: Enhanced TimescaleDB storage metrics
          ticksWritten: svc.ticksWritten || 0,
          ticksBuffered: svc.ticksBuffered || 0,
          ticksFailed: svc.ticksFailed || 0,
          channelCapacity: svc.channelCapacity || 0,
          bufferUtilization: svc.bufferUtilization || 0,
          failureRate: svc.failureRate || 0
        };

        return service;
      });
      setServices(mappedServices);

      // Phase 10C.1 FIX: Use callback form to preserve SignalR real-time values
      setStats(prev => ({
        // Real-time metrics (updated by SignalR ONLY - every 5s):
        ticksPerSecond: prev.ticksPerSecond,  // Keep SignalR value, don't override
        spreadsPerMinute: prev.spreadsPerMinute,  // Keep SignalR value, don't override

        // Slow-changing metrics (updated by REST - every 30s):
        memoryUsageMB: Math.floor(metrics.memoryUsageMB || 0),
        uptimeHours: Math.floor(metrics.uptimeHours || 0),
        downtimeMinutes: Math.floor(metrics.downtimeMinutes || 0),
        status: health.status === 'Healthy' ? 'OPERATIONAL' : 'DEGRADED',
        cpu: metrics.cpuUsageFormatted || '0%',
        dataRate: prev.dataRate,  // Derived from ticksPerSecond, keep SignalR-based value
        activeSymbols: configs.length || 0,

        // Mock data (Phase 11 - TODO)
        backfillJobs: 5,
        backfillProgress: 87,
        gapsDetected: 12,
        gapsResolved: 9
      }));

      // Phase 10C.1: Sparkline now updated by SignalR, not REST
      // Removed sparkline update here to avoid conflict with SignalR real-time updates

      setLastUpdate(new Date());
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setLoading(false);
    }
  };

  // Helper: Calculate data rate from ticks/second
  const calculateDataRate = (ticksPerSec: number) => {
    const bytesPerTick = 100; // ~100 bytes per tick
    const mbPerSec = (ticksPerSec * bytesPerTick) / (1024 * 1024);
    return `${mbPerSec.toFixed(2)} MB/s`;
  };

  // Phase 1: Quality Indicator Logic
  const getQuality = (correlation: number, tickDeltaMs: number, zScore: number) => {
    // Excellent: Green border
    if (correlation >= 0.95 && tickDeltaMs <= 200 && Math.abs(zScore) <= 1) {
      return {
        level: 'excellent',
        border: '#10B981',
        emoji: '🟢',
        label: 'Excellent'
      };
    }

    // Warning: Amber border
    if (correlation >= 0.90 && tickDeltaMs <= 500 && Math.abs(zScore) <= 2) {
      return {
        level: 'warning',
        border: '#F59E0B',
        emoji: '🟡',
        label: 'Warning'
      };
    }

    // Critical: Red border
    return {
      level: 'critical',
      border: '#EF4444',
      emoji: '🔴',
      label: 'Critical'
    };
  };

  // Phase 1: Fetch Spread Data with Z-Score Calculation
  const fetchSpreadData = async () => {
    try {
      console.log('📊 [Phase 1] Fetching spread data...');

      // Get enabled configs
      const configsRes = await axios.get(`${baseURL}/api/betaspread/configs`);
      const enabledConfigs = configsRes.data.filter((c: any) => c.enabled);

      console.log(`📊 [Phase 1] Found ${enabledConfigs.length} enabled pairs:`, enabledConfigs.map((c: any) => c.pairName));

      // Fetch latest spread data for each enabled pair
      for (const config of enabledConfigs) {
        try {
          const latestRes = await axios.get(
            `${baseURL}/api/betaspread/${config.symbol1}/${config.symbol2}/latest`
          );
          const latest = latestRes.data;

          // Calculate Z-Score client-side
          const spread = latest.spread;
          const mean = (latest.spreadMin + latest.spreadMax) / 2;
          const stdDev = latest.spreadStdDev;
          const zScore = stdDev !== 0 ? (spread - mean) / stdDev : 0;

          // Calculate quality
          const quality = getQuality(latest.correlation, latest.tickDeltaMs, zScore);

          // Store in Map
          setBetaSpreadData(prev => {
            const newMap = new Map(prev);
            newMap.set(config.pairName, {
              ...latest,
              pairName: config.pairName,
              symbol1: config.symbol1,
              symbol2: config.symbol2,
              zScore,
              quality,
              timestamp: latest.time || latest.createdAt || new Date().toISOString() // Fix: Add timestamp field
            });
            return newMap;
          });

          console.log(`✅ [Phase 1] ${config.pairName}: spread=${spread.toFixed(5)}, z=${zScore.toFixed(2)}σ, quality=${quality.level}`);
        } catch (err: any) {
          console.error(`❌ [Phase 1] Failed to fetch ${config.pairName}:`, err.message);
        }
      }
    } catch (err: any) {
      console.error('❌ [Phase 1] Error fetching spread data:', err);
    }
  };

  // Phase 1.6B: Fetch 24H Statistics for all spread pairs
  const fetch24HStats = async () => {
    try {
      console.log('📊 [Phase 1.6B] Fetching 24H statistics for all pairs...');
      setStats24HLoading(true);

      // Fetch current spread configs
      const configsResult: any = await GetBetaSpreadConfigs();
      if (configsResult.error || !configsResult.data) {
        console.error('❌ [Phase 1.6B] Failed to fetch configs:', configsResult.error);
        setStats24HLoading(false);
        return;
      }

      const configs = configsResult.data.filter((c: any) => c.enabled);
      const statsPromises = configs.map(async (config: any) => {
        try {
          const result: any = await GetPairStatistics(config.symbol1, config.symbol2, 24);

          if (result.error || !result.data) {
            return {
              pairName: config.pairName,
              symbol1: config.symbol1,
              symbol2: config.symbol2,
              high24h: null,
              low24h: null,
              range24h: null,
              yesterdayHigh: null,
              yesterdayLow: null,
              yesterdayRange: null,
              avgVolatility: null,
              dataPoints: 0,
              lastUpdate: new Date(),
              error: result.error || 'No data available'
            };
          }

          const stats = result.data;
          return {
            pairName: config.pairName,
            symbol1: config.symbol1,
            symbol2: config.symbol2,
            high24h: stats.maxSpread || null,
            low24h: stats.minSpread || null,
            range24h: stats.maxSpread && stats.minSpread ? stats.maxSpread - stats.minSpread : null,
            yesterdayHigh: stats.yesterdayHigh || null,
            yesterdayLow: stats.yesterdayLow || null,
            yesterdayRange: stats.yesterdayHigh && stats.yesterdayLow ? stats.yesterdayHigh - stats.yesterdayLow : null,
            avgVolatility: stats.avgVolatility || stats.avgStdDev || null,
            dataPoints: stats.totalDataPoints || 0,
            lastUpdate: new Date()
          };
        } catch (err: any) {
          console.error(`❌ [Phase 1.6B] Error fetching stats for ${config.pairName}:`, err.message);
          return {
            pairName: config.pairName,
            symbol1: config.symbol1,
            symbol2: config.symbol2,
            high24h: null,
            low24h: null,
            range24h: null,
            yesterdayHigh: null,
            yesterdayLow: null,
            yesterdayRange: null,
            avgVolatility: null,
            dataPoints: 0,
            lastUpdate: new Date(),
            error: err.message
          };
        }
      });

      const allStats = await Promise.all(statsPromises);
      setPair24HStats(allStats);
      setStats24HLoading(false);
      console.log(`✅ [Phase 1.6B] Fetched 24H stats for ${allStats.length} pairs`);
    } catch (err: any) {
      console.error('❌ [Phase 1.6B] Error fetching 24H stats:', err);
      setStats24HLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    refreshIntervalRef.current = setInterval(() => {
      fetchDashboardData();
    }, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 1: Spread Data Polling (10-second interval)
  useEffect(() => {
    console.log('🚀 [Phase 1] Starting spread data polling...');
    fetchSpreadData(); // Initial fetch
    spreadDataIntervalRef.current = setInterval(() => {
      fetchSpreadData();
    }, 10000); // 10-second refresh

    return () => {
      console.log('🛑 [Phase 1] Stopping spread data polling...');
      if (spreadDataIntervalRef.current) {
        clearInterval(spreadDataIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 1.6B: 24H Statistics Polling (60-second interval)
  useEffect(() => {
    console.log('🚀 [Phase 1.6B] Starting 24H stats polling...');
    fetch24HStats(); // Initial fetch
    stats24HIntervalRef.current = setInterval(() => {
      fetch24HStats();
    }, 60000); // 60-second refresh (less frequent for 24H data)

    return () => {
      console.log('🛑 [Phase 1.6B] Stopping 24H stats polling...');
      if (stats24HIntervalRef.current) {
        clearInterval(stats24HIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 10B: SignalR Real-Time Metrics Connection
  useEffect(() => {
    console.log('🚀 [Phase 10B] Starting SignalR connection to MetricsHub...');

    // Create SignalR connection
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseURL}/hubs/metrics`, {
        withCredentials: false
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    signalRConnectionRef.current = connection;

    // Subscribe to MetricsUpdate event
    connection.on('MetricsUpdate', (metrics: any) => {
      console.log('📊 [Phase 10B] Received MetricsUpdate:', metrics);

      const tickRate = metrics.TickRatePerSecond || metrics.tickRatePerSecond || 0;
      const spreadsRate = metrics.SpreadsPerMinute || metrics.spreadsPerMinute || 0;

      // Phase 10C.1: Update real-time metrics + calculated data rate
      setStats(prev => ({
        ...prev,
        ticksPerSecond: tickRate,
        spreadsPerMinute: spreadsRate,
        dataRate: calculateDataRate(tickRate)  // Update data rate based on real-time ticks
      }));

      // Phase 10C.1: Update sparkline with real-time tick rate
      setSparklineData(prev => {
        const newValue = tickRate * 10; // Scale for visibility
        const newData = [...prev, Math.max(newValue, 10)]; // Min 10 for chart visibility
        return newData.slice(-50);
      });

      setLastUpdate(new Date());
    });

    // Handle connection events
    connection.on('SubscriptionConfirmed', (data: any) => {
      console.log('✅ [Phase 10B] Subscription confirmed:', data);
      setSignalRConnected(true);
    });

    connection.onreconnecting((error) => {
      console.log('🔄 [Phase 10B] SignalR reconnecting...', error);
      setSignalRConnected(false);
    });

    connection.onreconnected((connectionId) => {
      console.log('✅ [Phase 10B] SignalR reconnected:', connectionId);
      setSignalRConnected(true);
      // Re-subscribe after reconnection
      connection.invoke('SubscribeToMetrics').catch(err => {
        console.error('❌ [Phase 10B] Failed to re-subscribe:', err);
      });
    });

    connection.onclose((error) => {
      console.log('🛑 [Phase 10B] SignalR connection closed:', error);
      setSignalRConnected(false);
    });

    // Start connection and subscribe
    connection.start()
      .then(() => {
        console.log('✅ [Phase 10B] SignalR connected successfully');
        setSignalRConnected(true);
        // Subscribe to metrics group
        return connection.invoke('SubscribeToMetrics');
      })
      .then(() => {
        console.log('✅ [Phase 10B] Subscribed to MetricsHub');
      })
      .catch(err => {
        console.error('❌ [Phase 10B] SignalR connection failed:', err);
        setSignalRConnected(false);
      });

    // Cleanup on unmount
    return () => {
      console.log('🛑 [Phase 10B] Stopping SignalR connection...');
      if (signalRConnectionRef.current) {
        signalRConnectionRef.current.invoke('UnsubscribeFromMetrics')
          .catch(() => {})
          .finally(() => {
            signalRConnectionRef.current?.stop();
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTimeAgo = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  // KPI Card Component - ULTRA COMPACT BLOTTER STYLE
  const KPICard: React.FC<{
    emoji: string;
    title: string;
    value: number | string;
    subtitle: string;
    gradient: string;
    shadowColor: string;
  }> = ({ title, value, subtitle, gradient, shadowColor}) => (
    <div style={{
      background: gradient,
      borderRadius: '6px',
      padding: '10px',
      boxShadow: `0 4px 10px ${shadowColor}`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'pointer',
      minHeight: '95px',
      border: '1px solid rgba(255, 255, 255, 0.08)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 6px 14px ${shadowColor}`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = `0 4px 10px ${shadowColor}`;
    }}
    >
      {/* Subtle Background Glow - Reduced */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '100px',
        height: '100px',
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: '50%',
        filter: 'blur(25px)'
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Title Only - NO EMOJI */}
        <div style={{
          fontSize: '8px',
          color: 'rgba(255, 255, 255, 0.9)',
          textTransform: 'uppercase',
          letterSpacing: '0.7px',
          fontWeight: '700',
          marginBottom: '8px',
          wordWrap: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: '1.2'
        }}>
          {title}
        </div>

        {/* Value */}
        <div style={{
          fontSize: '28px',
          fontWeight: '800',
          color: 'white',
          fontFamily: '"Courier New", "JetBrains Mono", monospace',
          lineHeight: '1',
          marginBottom: '6px',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.15)'
        }}>
          {loading ? '...' : typeof value === 'number' ? value.toLocaleString() : value}
        </div>

        {/* Subtitle */}
        <div style={{
          fontSize: '11px',
          color: 'rgba(255, 255, 255, 0.75)',
          fontWeight: '500',
          letterSpacing: '0.1px',
          wordWrap: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: '1.3'
        }}>
          {subtitle}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div style={{
        backgroundColor: '#0F172A',
        minHeight: 'calc(100vh - 100px)',
        color: '#E2E8F0',
        padding: '12px 20px',
        position: 'relative'
      }}>
        {/* Top Right Corner - Admin Console Label - ULTRA COMPACT */}
        <div style={{
          position: 'fixed',
          top: '70px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          borderRadius: '6px',
          border: '1px solid #334155',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          zIndex: 1000
        }}>
          <span style={{ fontSize: '14px', color: '#60A5FA' }}>⚙️</span>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: '#E2E8F0',
            textTransform: 'uppercase',
            letterSpacing: '0.4px'
          }}>
            Admin Console
          </span>

          {/* Phase 10C.3: Connection status indicator */}
          {signalRConnected && (
            <span style={{
              fontSize: '9px',
              color: '#10B981',
              marginLeft: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#10B981',
                boxShadow: '0 0 8px #10B981',
                animation: 'pulse 2s ease-in-out infinite'
              }} />
              LIVE
            </span>
          )}
        </div>

        {/* Hero Section - ULTRA COMPRESSED FOR NO-SCROLL */}
        <div style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          borderRadius: '6px',
          padding: '10px 14px',
          marginBottom: '8px',
          marginTop: '2px',
          overflow: 'hidden',
          border: '1px solid #334155',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Subtle Glow Only - NO GRID */}
          <div style={{
            position: 'absolute',
            top: '-30%',
            right: '-5%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
            filter: 'blur(40px)',
            zIndex: 0,
            pointerEvents: 'none'
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ marginBottom: '8px' }}>
              <h1 style={{
                fontSize: '22px',
                fontWeight: '800',
                marginBottom: '4px',
                background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 50%, #2563EB 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span>
                QuantXFeed
              </h1>
              <p style={{
                fontSize: '10px',
                color: '#94A3B8',
                marginTop: '4px',
                fontWeight: '400',
                lineHeight: '1.4',
                wordWrap: 'break-word',
                overflowWrap: 'anywhere'
              }}>
                Real-time monitoring and management of DataFeed server infrastructure
                {!loading && <span style={{ marginLeft: '12px', fontSize: '10px', color: '#64748B', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  • Updated {getTimeAgo()}
                </span>}
              </p>
            </div>

            {/* System Stats Bar - ULTRA COMPACT */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '8px',
              padding: '6px 10px',
              backgroundColor: 'rgba(15, 23, 42, 0.85)',
              borderRadius: '6px',
              border: '1px solid #334155'
            }}>
              <div>
                <div style={{ fontSize: '10px', color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: '600' }}>STATUS</div>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '700',
                  color: stats.status === 'OPERATIONAL' ? '#34D399' : '#FBBF24',
                  transition: 'color 0.3s ease'
                }}>{stats.status}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: '600' }}>CPU USAGE</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#60A5FA' }}>{stats.cpu}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: '600' }}>DATA RATE</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#A78BFA' }}>{stats.dataRate}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: '600' }}>SYMBOLS</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#FBBF24' }}>{stats.activeSymbols}</div>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Section Header - ULTRA MINIMAL */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '6px'
        }}>
          <span style={{ fontSize: '18px' }}>📊</span>
          <h2 style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#E2E8F0',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            margin: 0
          }}>
            Key Performance Indicators
          </h2>
        </div>

        {/* KPI Cards Grid - ULTRA COMPACT */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
          marginBottom: '6px'
        }}>
          {/* CARD 1: TICKS/SECOND - Enhanced with target check and total count */}
          <KPICard
            emoji="⚡"
            title="⚡ TICKS/SEC"
            value={stats.ticksPerSecond}
            subtitle={`⟲ Live (5s) • ${stats.ticksPerSecond >= 5 ? '✓' : '⚠️'} Target: 5+/s`}
            gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            shadowColor="rgba(16, 185, 129, 0.3)"
          />
          {/* CARD 2: SPREADS/MIN - Enhanced with ALL PAIRS label and active pairs count */}
          <KPICard
            emoji="📈"
            title="📈 SPREADS/MIN (ALL PAIRS)"
            value={stats.spreadsPerMinute}
            subtitle={`⟲ Live (5s) • ${betaSpreadData.size || stats.activeSymbols} pairs active`}
            gradient="linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
            shadowColor="rgba(59, 130, 246, 0.3)"
          />
          {/* CARD 3: MEMORY - Enhanced with dynamic health status */}
          <KPICard
            emoji="💾"
            title="💾 MEMORY (MB)"
            value={stats.memoryUsageMB}
            subtitle={`${Math.round((stats.memoryUsageMB / 4096) * 100)}% of 4096 MB • ${stats.memoryUsageMB > 3480 ? '⚠️ High' : stats.memoryUsageMB > 2867 ? '⚡ Moderate' : '✓ Healthy'}`}
            gradient="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
            shadowColor="rgba(139, 92, 246, 0.3)"
          />
          {/* CARD 4: UPTIME - Enhanced with formatted time display */}
          <KPICard
            emoji="⏱️"
            title="⏱️ UPTIME"
            value={(() => {
              const days = Math.floor(stats.uptimeHours / 24);
              const hrs = Math.floor(stats.uptimeHours % 24);
              const mins = Math.floor((stats.uptimeHours * 60) % 60);
              return `${days}d ${hrs}h ${mins}m`;
            })()}
            subtitle={`Since: ${new Date(Date.now() - stats.uptimeHours * 3600000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} UTC`}
            gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
            shadowColor="rgba(245, 158, 11, 0.3)"
          />
          {/* CARD 5: DOWNTIME - Enhanced with 30D label and uptime percentage */}
          <KPICard
            emoji="⚠️"
            title="⚠️ DOWNTIME (30D)"
            value={stats.downtimeMinutes}
            subtitle={`✓ ${((43200 - stats.downtimeMinutes) / 43200 * 100).toFixed(2)}% Uptime`}
            gradient="linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
            shadowColor="rgba(239, 68, 68, 0.3)"
          />
          {/* CARD 6: ACTIVE PAIRS - Enhanced with icon and live indicator */}
          <KPICard
            emoji="🔗"
            title="🔗 ACTIVE PAIRS"
            value={betaSpreadData.size || stats.activeSymbols}
            subtitle="⟲ Live • See details below ↓"
            gradient="linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)"
            shadowColor="rgba(20, 184, 166, 0.3)"
          />
        </div>

        {/* Backfill & Gap Statistics - ULTRA COMPACT */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '6px'
        }}>
          {/* Backfill Operations */}
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '6px',
            padding: '10px',
            border: '1px solid #334155',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            opacity: 0.6,
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '9px',
              color: '#FFA500',
              backgroundColor: '#1E293B',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid #FFA500',
              fontWeight: '600'
            }}>
              PHASE 11 - PLACEHOLDER
            </div>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>🔄</span> Backfill Operations
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '6px', fontWeight: '600' }}>Active Jobs</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#3B82F6', fontFamily: '"Courier New", monospace' }}>
                  {stats.backfillJobs}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '6px', fontWeight: '600' }}>Progress</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#10B981', fontFamily: '"Courier New", monospace' }}>
                  {stats.backfillProgress}%
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{
                width: '100%',
                height: '10px',
                backgroundColor: '#0F172A',
                borderRadius: '5px',
                overflow: 'hidden',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{
                  width: `${stats.backfillProgress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #3B82F6 0%, #10B981 100%)',
                  transition: 'width 0.5s ease',
                  boxShadow: '0 0 8px rgba(59, 130, 246, 0.5)'
                }} />
              </div>
              <div style={{ fontSize: '11px', color: '#64748B', marginTop: '8px', textAlign: 'right', fontWeight: '500' }}>
                {stats.backfillJobs} jobs in progress
              </div>
            </div>
          </div>

          {/* Gap Detection */}
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '6px',
            padding: '10px',
            border: '1px solid #334155',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            opacity: 0.6,
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '9px',
              color: '#FFA500',
              backgroundColor: '#1E293B',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid #FFA500',
              fontWeight: '600'
            }}>
              PHASE 11 - PLACEHOLDER
            </div>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>🔍</span> Gap Detection
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '6px', fontWeight: '600' }}>Detected</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#F59E0B', fontFamily: '"Courier New", monospace' }}>
                  {stats.gapsDetected}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '6px', fontWeight: '600' }}>Resolved</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#10B981', fontFamily: '"Courier New", monospace' }}>
                  {stats.gapsResolved}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px' }}>
                <span style={{ color: '#94A3B8', fontWeight: '600' }}>Resolution Rate</span>
                <span style={{ color: '#10B981', fontWeight: '700' }}>
                  {Math.round((stats.gapsResolved / stats.gapsDetected) * 100) || 0}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '10px',
                backgroundColor: '#0F172A',
                borderRadius: '5px',
                overflow: 'hidden',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{
                  width: `${Math.round((stats.gapsResolved / stats.gapsDetected) * 100) || 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #F59E0B 0%, #10B981 100%)',
                  transition: 'width 0.5s ease',
                  boxShadow: '0 0 8px rgba(245, 158, 11, 0.5)'
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* System Services - ULTRA COMPACT */}
        <div style={{ marginBottom: '6px' }}>
          <h2 style={{
            fontSize: '14px',
            fontWeight: '700',
            marginBottom: '12px',
            color: '#E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px' }}>🖥️</span> System Services
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {services.map((service, index) => (
              <div key={index} style={{
                backgroundColor: '#1E293B',
                borderRadius: '6px',
                padding: '12px',
                border: '1px solid #334155',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3B82F6';
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#334155';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#E2E8F0' }}>
                    {service.name}
                  </h3>
                  <div style={{
                    width: '11px',
                    height: '11px',
                    borderRadius: '50%',
                    backgroundColor: service.status === 'healthy' ? '#10B981' : '#EF4444',
                    boxShadow: `0 0 10px ${service.status === 'healthy' ? '#10B981' : '#EF4444'}`,
                    animation: service.status === 'healthy' ? 'pulse 2s ease-in-out infinite' : 'none'
                  }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: '#94A3B8', fontWeight: '500' }}>Status:</span>
                    <span style={{ color: '#34D399', fontWeight: '700', textTransform: 'capitalize' }}>{service.status}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: '#94A3B8', fontWeight: '500' }}>Port:</span>
                    <span style={{ color: '#E2E8F0', fontFamily: '"Courier New", monospace', fontWeight: '600' }}>{service.port}</span>
                  </div>
                  {service.latency && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#94A3B8', fontWeight: '500' }}>Latency:</span>
                      <span style={{ color: '#E2E8F0', fontFamily: '"Courier New", monospace', fontWeight: '600' }}>{service.latency}</span>
                    </div>
                  )}
                  {service.connections && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#94A3B8', fontWeight: '500' }}>Connections:</span>
                      <span style={{ color: '#E2E8F0', fontFamily: '"Courier New", monospace', fontWeight: '600' }}>{service.connections}</span>
                    </div>
                  )}
                  {/* Only show Ticks Received for gRPC Ingestion Service */}
                  {service.ticksReceived !== undefined && service.name === 'gRPC Ingestion Service' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#94A3B8', fontWeight: '500' }}>Ticks Received:</span>
                      <span style={{ color: '#10B981', fontFamily: '"Courier New", monospace', fontWeight: '700' }}>{service.ticksReceived.toLocaleString()}</span>
                    </div>
                  )}
                  {/* Only show Spreads Calculated for BetaSpread Calculator */}
                  {service.spreadsCalculated !== undefined && service.name === 'BetaSpread Calculator' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#94A3B8', fontWeight: '500' }}>Spreads Calculated:</span>
                      <span style={{ color: '#10B981', fontFamily: '"Courier New", monospace', fontWeight: '700' }}>{service.spreadsCalculated.toLocaleString()}</span>
                    </div>
                  )}
                  {/* Only show Rejected for BetaSpread Calculator */}
                  {service.spreadsRejected !== undefined && service.spreadsRejected > 0 && service.name === 'BetaSpread Calculator' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#94A3B8', fontWeight: '500' }}>Rejected:</span>
                      <span style={{ color: '#EF4444', fontFamily: '"Courier New", monospace', fontWeight: '700' }}>{service.spreadsRejected}</span>
                    </div>
                  )}

                  {/* Phase 1.6D: PROFESSIONAL gRPC INGESTION SERVICE CARD */}
                  {service.name === 'gRPC Ingestion Service' && service.ticksReceived !== undefined && (
                    <>
                      {/* SECTION 1: THROUGHPUT */}
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#94A3B8',
                          marginBottom: '6px',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          📊 THROUGHPUT
                        </div>
                        {/* Received */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Received:</span>
                          <span style={{
                            color: '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.ticksReceived.toLocaleString()} ✓
                          </span>
                        </div>
                        {/* Rate */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Rate:</span>
                          <span style={{
                            color: '#3B82F6',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {stats.ticksPerSecond.toFixed(1)}/sec ⟲ {stats.ticksPerSecond >= 5 ? '✅' : '⚠️'}
                          </span>
                        </div>
                        {/* Drop Rate */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '3px',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Drop Rate:</span>
                          <span style={{
                            color: (service.dropRate || 0) > 1 ? '#EF4444' : (service.dropRate || 0) > 0.1 ? '#F59E0B' : '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {(service.dropRate || 0).toFixed(2)}% {(service.dropRate || 0) > 1 ? '⚠️' : (service.dropRate || 0) > 0.1 ? '⚡' : '✅'}
                          </span>
                        </div>
                      </div>

                      {/* SECTION 2: DATA QUALITY */}
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#94A3B8',
                          marginBottom: '6px',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          🎯 DATA QUALITY
                        </div>
                        {/* Valid */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Valid:</span>
                          <span style={{
                            color: '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.ticksValid?.toLocaleString()} ({service.ticksValid && service.ticksReceived ? ((service.ticksValid / service.ticksReceived) * 100).toFixed(2) : '0.00'}%)
                          </span>
                        </div>
                        {/* Dropped */}
                        {service.ticksDropped !== undefined && service.ticksDropped > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            marginBottom: '5px'
                          }}>
                            <span style={{ color: '#94A3B8', fontWeight: '500' }}>Dropped:</span>
                            <span style={{
                              color: '#EF4444',
                              fontFamily: '"Courier New", monospace',
                              fontWeight: '700'
                            }}>
                              {service.ticksDropped.toLocaleString()} (Invalid)
                            </span>
                          </div>
                        )}
                        {/* Filtered */}
                        {service.ticksFiltered !== undefined && service.ticksFiltered > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            marginBottom: '3px'
                          }}>
                            <span style={{ color: '#94A3B8', fontWeight: '500' }}>Filtered:</span>
                            <span style={{
                              color: '#64748B',
                              fontFamily: '"Courier New", monospace',
                              fontWeight: '700'
                            }}>
                              {service.ticksFiltered.toLocaleString()} 🔇
                            </span>
                          </div>
                        )}
                      </div>

                      {/* FOOTER: Link to KPI */}
                      <div style={{
                        fontSize: '10px',
                        color: '#64748B',
                        marginTop: '8px',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        💡 Links to ⚡ TICKS/SEC KPI above ↑
                      </div>
                    </>
                  )}

                  {/* Phase 1.6A.3: Enhanced metrics for BetaSpread Calculator */}
                  {service.name === 'BetaSpread Calculator' && service.spreadsCalculated && (
                    <>
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#94A3B8',
                          marginBottom: '4px',
                          fontWeight: '600'
                        }}>
                          📊 Calculation Metrics:
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '4px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>⚡ Rate:</span>
                          <span style={{
                            color: '#3B82F6',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {stats.spreadsPerMinute}/min ⟲
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '4px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>✓ Accepted:</span>
                          <span style={{
                            color: '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.spreadsCalculated.toLocaleString()}
                            {' '}({Math.round((service.spreadsCalculated / (service.spreadsCalculated + (service.spreadsRejected || 0))) * 100)}%)
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>⚠️ Rejected:</span>
                          <span style={{
                            color: '#EF4444',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {(service.spreadsRejected || 0).toLocaleString()}
                            {' '}({Math.round(((service.spreadsRejected || 0) / (service.spreadsCalculated + (service.spreadsRejected || 0))) * 100)}%)
                          </span>
                        </div>

                        {/* Acceptance Rate Progress Bar */}
                        <div style={{ marginTop: '8px' }}>
                          <div style={{
                            fontSize: '11px',
                            color: '#94A3B8',
                            marginBottom: '4px',
                            fontWeight: '600'
                          }}>
                            📈 Acceptance Rate: {Math.round((service.spreadsCalculated / (service.spreadsCalculated + (service.spreadsRejected || 0))) * 100)}%
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#0F172A',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.round((service.spreadsCalculated / (service.spreadsCalculated + (service.spreadsRejected || 0))) * 100)}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#64748B',
                            marginTop: '4px',
                            fontStyle: 'italic'
                          }}>
                            💡 Links to SPREADS/MIN KPI above ↑
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Phase 1.6E: PROFESSIONAL TIMESCALEDB SERVICE CARD */}
                  {service.name === 'TimescaleDB' && service.ticksWritten !== undefined && (
                    <>
                      {/* SECTION 1: STORAGE METRICS */}
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#94A3B8',
                          marginBottom: '6px',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          📊 STORAGE METRICS
                        </div>
                        {/* Written */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Written:</span>
                          <span style={{
                            color: '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.ticksWritten.toLocaleString()} ✓
                          </span>
                        </div>
                        {/* Buffered */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Buffered:</span>
                          <span style={{
                            color: '#3B82F6',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.ticksBuffered?.toLocaleString() || 0} (in queue)
                          </span>
                        </div>
                        {/* Failed */}
                        {service.ticksFailed !== undefined && service.ticksFailed > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            marginBottom: '3px',
                            alignItems: 'center'
                          }}>
                            <span style={{ color: '#94A3B8', fontWeight: '500' }}>Failed:</span>
                            <span style={{
                              color: '#EF4444',
                              fontFamily: '"Courier New", monospace',
                              fontWeight: '700'
                            }}>
                              {service.ticksFailed.toLocaleString()} ⚠️
                            </span>
                          </div>
                        )}
                      </div>

                      {/* SECTION 2: DATABASE HEALTH */}
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#94A3B8',
                          marginBottom: '6px',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          🔌 DATABASE HEALTH
                        </div>
                        {/* Latency */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Latency:</span>
                          <span style={{
                            color: service.latency && parseFloat(service.latency) > 10 ? '#EF4444' : parseFloat(service.latency || '0') > 5 ? '#F59E0B' : '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {service.latency} {service.latency && parseFloat(service.latency) > 10 ? '⚠️' : parseFloat(service.latency || '0') > 5 ? '⚡' : '✅'}
                          </span>
                        </div>
                        {/* Buffer Utilization */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          marginBottom: '5px',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: '#94A3B8', fontWeight: '500' }}>Buffer:</span>
                          <span style={{
                            color: (service.bufferUtilization || 0) > 80 ? '#EF4444' : (service.bufferUtilization || 0) > 50 ? '#F59E0B' : '#10B981',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '700'
                          }}>
                            {(service.bufferUtilization || 0).toFixed(2)}% {(service.bufferUtilization || 0) > 80 ? '⚠️' : (service.bufferUtilization || 0) > 50 ? '⚡' : '✅'}
                          </span>
                        </div>
                        {/* Channel Capacity */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '11px',
                          marginBottom: '3px'
                        }}>
                          <span style={{ color: '#64748B', fontWeight: '500' }}>Capacity:</span>
                          <span style={{
                            color: '#64748B',
                            fontFamily: '"Courier New", monospace',
                            fontWeight: '600'
                          }}>
                            {service.ticksBuffered?.toLocaleString() || 0}/{service.channelCapacity?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>

                      {/* FOOTER: Link */}
                      <div style={{
                        fontSize: '10px',
                        color: '#64748B',
                        marginTop: '8px',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        💡 Stores all validated ticks from gRPC ↑
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Activity and Active Symbols - ULTRA COMPACT */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
          {/* Live Activity Chart */}
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '6px',
            padding: '10px',
            border: '1px solid #334155',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>📊</span> Live Activity
            </h2>
            <div style={{ position: 'relative', height: '150px' }}>
              <svg width="600" height="150" viewBox="0 0 600 150" style={{ display: 'block', width: '100%' }}>
                {[0, 1, 2, 3].map((i) => (
                  <line
                    key={i}
                    x1="0"
                    y1={i * 37.5}
                    x2="600"
                    y2={i * 37.5}
                    stroke="#334155"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                ))}
                {sparklineData.length > 1 && (
                  <>
                    <polyline
                      points={sparklineData.map((value, index) => {
                        const x = (index / (sparklineData.length - 1)) * 600;
                        const y = 150 - (value / 100) * 150;
                        return `${x},${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polygon
                      points={`0,150 ${sparklineData.map((value, index) => {
                        const x = (index / (sparklineData.length - 1)) * 600;
                        const y = 150 - (value / 100) * 150;
                        return `${x},${y}`;
                      }).join(' ')} 600,150`}
                      fill="url(#gradient)"
                      opacity="0.25"
                    />
                  </>
                )}
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div style={{ marginTop: '14px', fontSize: '12px', color: '#64748B', textAlign: 'center', fontWeight: '500' }}>
              Last 5 minutes {sparklineData.length > 0 && `• ${sparklineData.length} data points`}
            </div>
          </div>
        </div>

        {/* Phase 1.6B: BetaSpread 24H Monitor - Full Width Card */}
        <div style={{
          backgroundColor: '#1E293B',
          borderRadius: '6px',
          padding: '10px',
          border: '1px solid #334155',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          marginBottom: '8px'
        }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>📊</span> BetaSpread 24H Monitor
              {stats24HLoading && (
                <span style={{ fontSize: '11px', color: '#FBBF24', marginLeft: 'auto' }}>
                  ⟲ Refreshing...
                </span>
              )}
            </h2>

            {/* 24H Statistics Cards Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {pair24HStats.length === 0 && !stats24HLoading && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#64748B',
                  fontSize: '12px'
                }}>
                  No spread pairs configured or no data available
                </div>
              )}

              {pair24HStats.map((pair) => (
                <div
                  key={pair.pairName}
                  style={{
                    backgroundColor: '#0F172A',
                    borderRadius: '6px',
                    padding: '10px',
                    border: '1px solid #334155',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Pair Name Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#E2E8F0',
                      fontFamily: '"Courier New", monospace'
                    }}>
                      {pair.pairName}
                    </div>
                    <div style={{
                      fontSize: '10px',
                      color: '#64748B',
                      fontFamily: '"Courier New", monospace'
                    }}>
                      {pair.symbol1} / {pair.symbol2}
                    </div>
                  </div>

                  {/* Error State */}
                  {pair.error && (
                    <div style={{
                      fontSize: '11px',
                      color: '#F87171',
                      padding: '6px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '4px',
                      border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}>
                      ⚠️ {pair.error}
                    </div>
                  )}

                  {/* 24H Statistics Display */}
                  {!pair.error && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '8px'
                    }}>
                      {/* 24H High */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          24H High
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#10B981',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.high24h !== null ? (pair.high24h >= 0 ? '+' : '') + pair.high24h.toFixed(5) : 'N/A'}
                        </div>
                      </div>

                      {/* 24H Low */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          24H Low
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#EF4444',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.low24h !== null ? (pair.low24h >= 0 ? '+' : '') + pair.low24h.toFixed(5) : 'N/A'}
                        </div>
                      </div>

                      {/* 24H Range */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          Range (Δ)
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: pair.range24h && pair.range24h > 1.0 ? '#FBBF24' : '#60A5FA',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.range24h !== null ? pair.range24h.toFixed(5) : 'N/A'}
                          {pair.range24h && pair.range24h > 1.0 && ' ⚠️'}
                        </div>
                      </div>

                      {/* Avg Volatility */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          Avg Vol
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#A78BFA',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.avgVolatility !== null ? pair.avgVolatility.toFixed(4) : 'N/A'}
                        </div>
                      </div>

                      {/* Yesterday High */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          Yesterday High
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#10B981',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.yesterdayHigh !== null ? (pair.yesterdayHigh >= 0 ? '+' : '') + pair.yesterdayHigh.toFixed(5) : 'N/A'}
                        </div>
                      </div>

                      {/* Yesterday Low */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          Yesterday Low
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#EF4444',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.yesterdayLow !== null ? (pair.yesterdayLow >= 0 ? '+' : '') + pair.yesterdayLow.toFixed(5) : 'N/A'}
                        </div>
                      </div>

                      {/* Yesterday Range */}
                      <div style={{
                        backgroundColor: '#1E293B',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8',
                          marginBottom: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '600'
                        }}>
                          Yesterday Range
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#F59E0B',
                          fontFamily: '"Courier New", monospace'
                        }}>
                          {pair.yesterdayRange !== null ? pair.yesterdayRange.toFixed(5) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Data Points Footer */}
                  {!pair.error && (
                    <div style={{
                      marginTop: '6px',
                      fontSize: '10px',
                      color: '#64748B',
                      textAlign: 'right',
                      fontStyle: 'italic'
                    }}>
                      {pair.dataPoints} data points • Last update: {pair.lastUpdate.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer Note */}
            <div style={{
              marginTop: '10px',
              fontSize: '11px',
              color: '#64748B',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              📊 24-hour rolling window • Updates every 60s
            </div>
          </div>

          {/* Phase 3: Professional Spread Analytics Cards - NEW */}
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '6px',
            padding: '10px',
            border: '1px solid #334155',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>💹</span> Active Spread Pairs (Live Analytics)
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {Array.from(betaSpreadData.entries()).map(([pairName, data]) => (
                <ProfessionalSpreadCard
                  key={pairName}
                  data={data}
                  isExpanded={expandedPairs.has(pairName)}
                  onToggle={() => {
                    setExpandedPairs(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(pairName)) {
                        newSet.delete(pairName);
                      } else {
                        newSet.add(pairName);
                      }
                      return newSet;
                    });
                  }}
                />
              ))}
              {betaSpreadData.size === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#64748B',
                  fontSize: '12px'
                }}>
                  Loading spread data...
                </div>
              )}
            </div>
          </div>

        {/* Action Bar - ULTRA COMPACT BLOTTER STYLE */}
        <div style={{
          backgroundColor: '#1E293B',
          borderRadius: '6px',
          padding: '10px',
          border: '1px solid #334155',
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }}>
          <button style={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 24px',
            fontSize: '13px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 18px rgba(59, 130, 246, 0.45)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.35)';
          }}
          onClick={() => window.location.href = '/backfill'}
          >
            <span style={{ fontSize: '18px' }}>🔄</span> Start Backfill
          </button>

          <button style={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '14px 32px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 18px rgba(245, 158, 11, 0.45)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(245, 158, 11, 0.35)';
          }}
          onClick={() => window.location.href = '/gaps'}
          >
            <span style={{ fontSize: '18px' }}>🔍</span> Detect Gaps
          </button>

          <button style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '14px 32px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 18px rgba(16, 185, 129, 0.45)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(16, 185, 129, 0.35)';
          }}
          onClick={async () => {
            console.log('🔄 Refresh Now button clicked!');
            setRefreshing(true);
            await fetchDashboardData();
            setTimeout(() => setRefreshing(false), 500);
          }}
          disabled={refreshing}
          >
            <span style={{ fontSize: '18px', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
            {refreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>

        {/* Animations */}
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default Dashboard;
